import { Css, IconButton, Palette, px, TestIds, useTestIds } from "@homebound/beam";
import { RuntimeStyle } from "@homebound/truss/runtime";
import { useMemo } from "react";
import { Percentage, Price } from "src/components";
import { InvoiceStatus, InvoiceType } from "src/generated/graphqlTypes";
import { PageHeader } from "src/routes/layout/PageHeader";
import {
  InvoicePandaDocPdf_AccruedLineItemFragment,
  InvoicePandaDocPdf_InvoiceFragment,
  InvoicePandaDocPdf_InvoiceLineItemFragment,
  useInvoicePandaDocPdfQuery,
} from "src/routes/pdfs/InvoicePdf/InvoicePandaDocPdf.generated";
import { calcPercent, capitalize, groupBy, queryResult } from "src/utils";
import { formatWithYear } from "src/utils/dates";
import { StringParam, useQueryParam } from "use-query-params";

export function InvoicePdf() {
  const [invoiceId] = useQueryParam("invoiceId", StringParam);
  const query = useInvoicePandaDocPdfQuery({ variables: { invoiceId: invoiceId! } });
  return queryResult(query, ({ invoice }) => <InvoicePdfView invoice={invoice} />);
}

function InvoicePdfView({ invoice }: { invoice: InvoicePandaDocPdf_InvoiceFragment }) {
  const { primaryHomeowner, customer, buildAddress, companyInfo } = invoice.project;
  const testIds = useTestIds({}, "invoicePdf");

  // Calculate the total percentage of completion invoiced to date, excluding down payment applications
  const allPocInvoicedToDateInCents = useMemo(() => {
    const pocInvoices = invoice.project.stages
      .flatMap((stage) => stage.invoices)
      .filter((inv) => inv.type.code === InvoiceType.PercentageOfCompletion)
      .filter((inv) => ![InvoiceStatus.Void, InvoiceStatus.Rejected].includes(inv.status.code))
      // Use creation date as the invoice date can change upon approval
      .filter((inv) => !inv.createdAt.isAfter(invoice.createdAt));
    const downPaymentApplicationsInCents = pocInvoices
      .flatMap((inv) => inv.lineItems)
      .filter((li) => li.downPaymentInvoice)
      .sum((li) => li.amountInCents);
    return pocInvoices.sum((inv) => inv.amountInCents) - downPaymentApplicationsInCents;
  }, [invoice]);

  return (
    <>
      <RuntimeStyle
        css={{ "@page": { size: "letter" }, ul: Css.my2.ml1.add("listStyleType", "disc").$, li: Css.my2.$ }}
      />

      <div css={Css.p2.$}>
        <PageHeader
          noBorder
          left={
            <div>
              <img src="/wordmark.svg" alt="Homebound" css={Css.h(px(20)).$} />
              <div {...testIds.invoiceNumber} css={Css.xl2.tal.mt2.$}>
                Invoice #{invoice.invoiceNumber}
              </div>
            </div>
          }
          right={
            <>
              {invoice.document?.asset.downloadUrl && (
                <IconButton
                  color={Palette.Blue500}
                  inc={3}
                  icon="download"
                  onClick={invoice.document?.asset.downloadUrl}
                />
              )}
            </>
          }
        />

        <div css={Css.mt4.df.jcsb.$}>
          <div>
            <div {...testIds.projectAddress}>{buildAddress?.street1}</div>
            <div {...testIds.invoiceDate}>Invoice Date: {formatWithYear(invoice.invoiceDate)}</div>
            <div {...testIds.invoiceDueDate}>Due Date: {formatWithYear(invoice.dueDate)}</div>
            <div>ar@homebound.com</div>
          </div>

          <div css={Css.df.$}>
            <div css={Css.mr2.$}>Bill to:</div>
            <div>
              <div {...testIds.nameAndAddress} css={Css.smSb.$}>
                {customer?.name ?? primaryHomeowner?.fullName ?? "Client"} - {buildAddress?.street1}
              </div>
              <div {...testIds.streetAddress}>
                {buildAddress?.street1}, {buildAddress?.street2 ?? ""}
              </div>
              <div {...testIds.cityStateAndZip}>
                {buildAddress?.city}, {buildAddress?.state} {buildAddress?.postalCode}
              </div>
              {!customer && primaryHomeowner && <div {...testIds.homeownerEmail}>{primaryHomeowner.email}</div>}
            </div>
          </div>

          <div>
            <div css={Css.gray600.tar.$}>Total amount due</div>
            <div css={Css.xl2.$}>
              <Price
                id="totalAmountDue"
                valueInCents={invoice.amountInCents - (invoice.creditForInvoice?.amountInCents ?? 0)}
              />
            </div>
          </div>
        </div>

        {invoice.type.code === InvoiceType.PercentageOfCompletion ? (
          <div css={Css.mt4.smSb.$} {...testIds.progressBilling}>
            <p>{invoice.title}</p>
            <p>
              Overall project completion:{" "}
              <Percentage percent={calcPercent(allPocInvoicedToDateInCents, invoice.contractPriceSnapshotInCents)} />
            </p>
          </div>
        ) : null}
        <div css={Css.xl.my5.$}>Items</div>
        <div css={Css.px4.bb.bcGray200.pb2.mb2.$}>
          <div css={Css.w100.gray600.df.jcsb.mb2.$}>
            <div>Description</div>
            <div>Total Cost</div>
          </div>
          <InvoiceLineItems invoice={invoice} testId={testIds} />
        </div>

        <div css={Css.df.$}>
          <div css={Css.w50.tar.sm.$}>Total Amount Due</div>
          <div css={Css.w50.tar.pr4.sm.$}>
            <Price id="lineItemsTotalAmountDue" valueInCents={invoice.amountInCents} />
          </div>
        </div>

        {invoice.type.code === InvoiceType.PercentageOfCompletion ? (
          <ProjectPocInvoiceSummary
            testIds={testIds}
            invoice={invoice}
            allInvoicedToDate={allPocInvoicedToDateInCents}
          />
        ) : null}

        {/* Terms */}
        <div css={Css.mt8.$} style={{ pageBreakBefore: "avoid" }}>
          <p css={Css.mb1.$}>
            {companyInfo.licenseNumber} Fed ID# {companyInfo.federalId}
          </p>

          <p css={Css.mb1.$}>
            As a reminder, all invoices are due within the terms outlined in your contract. If your invoice is not paid
            by your due date, {companyInfo.name} will issue you a written notice that your account is overdue. Within
            three business days of issuing the notice, interest will begin to accrue in accordance with your contract.
            If payment is not received your due date, {companyInfo.name} reserves the right to stop work until payment
            is received.{" "}
          </p>

          <p>Send us a wire transfer to – </p>
          <p>JPMorgan Chase Bank</p>
          <p>270 Park Avenue</p>
          <p>New York, NY 10017</p>
          <p>Account Name: {companyInfo.name}</p>
          <p>Account Number: {companyInfo.accountNumber} </p>
          <p>Routing Number (Wires): 021000021</p>
          <p>Routing Number (ACH): 322271627 </p>
          <p css={Css.mb1.$}>Swift Code: CHASUS33</p>

          <p>Or mail us a check via regular US postal Service – </p>
          <p>{companyInfo.name}</p>
          <p css={Css.mb1.$}>P.O. Box 737959 Dallas, TX 75373-7959</p>

          <p>Or mail us a check via overnight courier service –</p>
          <p>{companyInfo.name}</p>
          <p>JPMorgan Chase (TX1-0029)</p>
          <p>Attn: Homebound Technologies 737959</p>
          <p>14800 Frye Road, 2nd Floor, Ft. Worth, TX 76155</p>
        </div>
      </div>
    </>
  );
}

type InvoiceLineItemsProps = { invoice: InvoicePandaDocPdf_InvoiceFragment; testId: TestIds };

function InvoiceLineItems({ invoice, testId }: InvoiceLineItemsProps) {
  const invoiceLineItems = invoice.lineItems.map((li) => {
    const { homeownerContractLineItem: hcli } = li;
    const name =
      hcli.projectItem.item.costCode.isPriceOnly && hcli.owner.__typename === "HomeownerContractChangeOrder"
        ? hcli.owner.name
        : hcli.projectItem.name;
    return { testId: "lineItem", name, amountInCents: li.amountInCents };
  });

  const drawLineItems = invoice.drawLineItems.map((dli) => ({
    testId: "drawLineItem",
    name: dli.draw.description,
    amountInCents: dli.draw.amountInCents,
  }));

  type GroupedInvoiceLineRow = InvoicePandaDocPdf_InvoiceLineItemFragment | InvoicePandaDocPdf_AccruedLineItemFragment;
  const [downPaymentApplications, others] = invoice.lineItems.partition((r) => !!r.downPaymentInvoice?.id);
  // PercentageOfCompletion and DownPayment invoices can't show lines breakdown by project item
  const rowsByNonStandardType: Record<InvoiceType, [string, GroupedInvoiceLineRow[]][]> = {
    [InvoiceType.PercentageOfCompletion]: [
      // Other lines (e.g. Architecture Fee, etc.) should not be shown on the PDF, summed up in the "Percentage of Completion" row
      ["Percentage of Completion", [...invoice.accruedLineItems, ...others]],
      ["Down Payment Application", downPaymentApplications],
    ],
    [InvoiceType.DownPayment]: [["Down Payment", invoice.lineItems]],
    [InvoiceType.Standard]: [], // Standard are handled individually
  };

  // Accrued and Final Invoice invoices are grouped by cost classification type
  const accruedAndFinalInvoiceRows = groupBy(
    [
      ...invoice.accruedLineItems.map((ali) => ({ ...ali, costClassification: ali.costClassificationType?.name! })),
      ...(invoice.isFinalInvoice ? invoice.lineItems : []).map((ili) => ({
        ...ili,
        costClassification: ili.homeownerContractLineItem?.projectItem.item.costCode.costClassification.name,
      })),
    ],
    (item) => `${capitalize(item.costClassification!)} Cost`,
  ).toEntries();

  const type = invoice.type.code;
  const aggregatedRows = (type !== InvoiceType.Standard ? rowsByNonStandardType[type] : accruedAndFinalInvoiceRows)
    .filter(([_, items]) => items.nonEmpty)
    .map(([name, items]) => ({
      name,
      amountInCents: items.reduce((sum, item) => sum + item.amountInCents, 0),
      testId: "aggregatedItem",
    }));

  return (
    <>
      {[...(aggregatedRows.nonEmpty ? aggregatedRows : [...invoiceLineItems, ...drawLineItems])].map((li) => (
        <div key={li.name} css={Css.df.jcsb.$}>
          <div {...testId[`${li.testId}Name`]}>{li.name}</div>
          <div>
            <Price id={`${li.testId}Amount`} valueInCents={li.amountInCents} />
          </div>
        </div>
      ))}
    </>
  );
}

type ProjectPocInvoiceSummaryProps = {
  invoice: InvoicePandaDocPdf_InvoiceFragment;
  testIds: TestIds;
  allInvoicedToDate: number;
};
function ProjectPocInvoiceSummary({ invoice, testIds, allInvoicedToDate }: ProjectPocInvoiceSummaryProps) {
  const { currentInvoiceAmount, priorInvoicesTotal, balanceToFinish } = useMemo(
    () => ({
      currentInvoiceAmount: invoice.amountInCents,
      priorInvoicesTotal: allInvoicedToDate - invoice.amountInCents,
      balanceToFinish: invoice.contractPriceSnapshotInCents - allInvoicedToDate,
    }),
    [invoice, allInvoicedToDate],
  );
  return (
    <>
      <div css={Css.xl.my5.$}>Summary</div>
      <div css={Css.mt4.px4.bb.bcGray200.pb2.$}>
        <div css={Css.w100.gray600.df.jcsb.mb2.$}>
          <div>Description</div>
          <div>Amount</div>
        </div>
        <div css={Css.df.jcsb.$}>
          <div {...testIds.totalContractLabel}>Total Contract + Approved Change Orders</div>
          <Price
            {...testIds.totalContractAmount}
            id="totalContract"
            valueInCents={invoice.contractPriceSnapshotInCents}
          />
        </div>
        <div css={Css.df.jcsb.$}>
          <div {...testIds.totalCompletedLabel}>Total Completed To Date</div>
          <Price {...testIds.totalCompletedAmount} id="totalCompleted" valueInCents={allInvoicedToDate} />
        </div>
        <div css={Css.df.jcsb.$}>
          <div {...testIds.lessPriorInvoicesLabel}>Less Prior Invoices</div>
          <Price {...testIds.lessPriorInvoicesAmount} id="lessPriorInvoices" valueInCents={priorInvoicesTotal} />
        </div>
        <div css={Css.df.jcsb.smSb.$}>
          <div {...testIds.amountDueThisInvoiceLabel}>Amount Due This Invoice</div>
          <Price
            {...testIds.amountDueThisInvoiceAmount}
            id="amountDueThisInvoice"
            valueInCents={currentInvoiceAmount}
          />
        </div>
        <div css={Css.df.jcsb.$}>
          <div {...testIds.balanceToFinishLabel}>Balance To Finish</div>
          <Price {...testIds.balanceToFinishAmount} id="balanceToFinish" valueInCents={balanceToFinish} />
        </div>
      </div>
    </>
  );
}
